import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { FiUpload } from "react-icons/fi";
import { appendConsumptionNodeToPipeline, getBankingPolicySlots } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import { parseCompactSlotCsvFile } from '../../../utils/parseCompactSlotCsv';
import './ConsumptionConfigModal.css';

Modal.setAppElement('#root');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_SLOT_ROWS = ['22 to 06', '9 to 12', '18 to 22', '06 to 09 & 12 to 18'];
const VALID_MONTH_COUNTS = new Set([1, 3, 6, 12]);
const EXISTING_OA_TYPES = [
  { id: 'solar', label: 'Solar' },
  { id: 'wind', label: 'Wind' },
  { id: 'hybrid', label: 'Hybrid' },
];
const normalizeExistingOaType = (value) => {
  const normalized = String(value || 'solar').trim().toLowerCase();
  return ['solar', 'wind', 'hybrid'].includes(normalized) ? normalized : 'solar';
};
const normalizeExistingOaInputMode = (value) => {
  const normalized = String(value || 'annual').trim().toLowerCase();
  return ['hourly_csv', 'hourly'].includes(normalized) ? 'hourly_csv' : 'annual';
};
const existingOaCsvHint = (oaType) => (
  oaType === 'hybrid'
    ? 'CSV must include Date, Time, and one generation column (for example Hybrid generation).'
    : `CSV must include Date, Time, and a ${oaType} generation column.`
);

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '920px',
    width: '94%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const makeEmptyGrid = (slots = DEFAULT_SLOT_ROWS, previous = {}) => slots.reduce((acc, slot) => {
  acc[slot] = MONTHS.reduce((row, month) => {
    row[month] = previous?.[slot]?.[month] ?? '';
    return row;
  }, {});
  return acc;
}, {});

const parseStoredSlotMonthly = (value) => {
  if (!value) return {};
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      return {};
    }
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.units)) {
    return {};
  }
  return normalizeSlotGrid(parsed);
};

export const normalizeSlotGrid = (slotMonthlyData = {}) => {
  if (!Array.isArray(slotMonthlyData)) return slotMonthlyData || {};

  return slotMonthlyData.reduce((grid, row) => {
    const slot = row?.Slot ?? row?.slot;
    if (!slot) return grid;
    grid[slot] = MONTHS.reduce((values, month) => {
      values[month] = row?.[month] ?? '';
      return values;
    }, {});
    return grid;
  }, {});
};

const makeUnitId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `unit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeMultiUnitBalanceMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['manual', 'user_input', 'no_permissible_limit'].includes(normalized) ? 'manual' : 'calculated';
};

export const makeConsumptionUnit = (index = 1, slots = DEFAULT_SLOT_ROWS, overrides = {}) => {
  const normalizedGrid = normalizeSlotGrid(overrides.slot_monthly_data);
  return {
    unit_id: overrides.unit_id || overrides.unitId || makeUnitId(),
    unit_name: overrides.unit_name || overrides.unitName || `Unit ${index}`,
    location: overrides.location || '',
    cd: overrides.cd ?? '',
    ct: overrides.ct ?? '',
    voltage: overrides.voltage ?? '',
    permissible_limit: overrides.permissible_limit ?? overrides.permissibleLimit ?? '',
    existing_oa: overrides.existing_oa ?? overrides.existingOa ?? '',
    existing_oa_re_units: overrides.existing_oa_re_units ?? overrides.existingOaReUnits ?? 0,
    existing_btm_units: overrides.existing_btm_units ?? overrides.existingBtmUnits ?? 0,
    existing_oa_type: normalizeExistingOaType(overrides.existing_oa_type ?? overrides.existingOaType),
    existing_oa_input_mode: normalizeExistingOaInputMode(overrides.existing_oa_input_mode ?? overrides.existingOaInputMode),
    existing_oa_hourly_file: overrides.existing_oa_hourly_file ?? overrides.existingOaHourlyFile ?? '',
    existing_oa_hourly_filename: overrides.existing_oa_hourly_filename ?? overrides.existingOaHourlyFilename ?? '',
    existing_oa_banking_enabled: Boolean(overrides.existing_oa_banking_enabled ?? overrides.existingOaBankingEnabled ?? false),
    existing_oa_banking_policy: overrides.existing_oa_banking_policy ?? overrides.existingOaBankingPolicy ?? '',
    balance: overrides.balance ?? 0,
    balance_mode: normalizeMultiUnitBalanceMode(overrides.balance_mode ?? overrides.balanceMode),
    allocation_share_override_pct: overrides.allocation_share_override_pct
      ?? overrides.allocationShareOverridePct
      ?? null,
    // Keep policy-specific rows until the policy request resolves and slotRows is known.
    slot_monthly_data: {
      ...normalizedGrid,
      ...makeEmptyGrid(slots, normalizedGrid),
    },
  };
};

const ConsumptionConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [inputMode, setInputMode] = useState('hourly_csv');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [slotCsvFile, setSlotCsvFile] = useState(null);
  const [slotCsvFileName, setSlotCsvFileName] = useState('');
  const [year, setYear] = useState(2025);
  const [bankingPolicies, setBankingPolicies] = useState([]);
  const [bankingPolicyName, setBankingPolicyName] = useState('GH_2025');
  const [existingOaReUnits, setExistingOaReUnits] = useState(initialData.existingOaReUnits ?? 0);
  const [existingBtmUnits, setExistingBtmUnits] = useState(initialData.existingBtmUnits ?? 0);
  const [existingOaType, setExistingOaType] = useState(() => normalizeExistingOaType(initialData.existingOaType));
  const [existingOaInputMode, setExistingOaInputMode] = useState(() => normalizeExistingOaInputMode(initialData.existingOaInputMode));
  const [existingOaFile, setExistingOaFile] = useState(null);
  const [existingOaFileName, setExistingOaFileName] = useState(initialData.existingOaFileName || '');
  const [existingOaBankingEnabled, setExistingOaBankingEnabled] = useState(Boolean(initialData.existingOaBankingEnabled));
  const [existingOaBankingPolicy, setExistingOaBankingPolicy] = useState(initialData.existingOaBankingPolicy || '');
  const [balanceMode, setBalanceMode] = useState(() => normalizeMultiUnitBalanceMode(initialData.balanceMode));
  const [grid, setGrid] = useState(() => makeEmptyGrid());
  const [units, setUnits] = useState(() => [makeConsumptionUnit(1)]);
  const [activeUnitId, setActiveUnitId] = useState(() => units[0]?.unit_id);
  const [unitSlotCsvNames, setUnitSlotCsvNames] = useState({});
  const [isParsingUnitCsv, setIsParsingUnitCsv] = useState(false);
  const fileInputRef = useRef(null);
  const slotFileInputRef = useRef(null);
  const multiUnitSlotFileInputRef = useRef(null);
  const existingOaFileInputRef = useRef(null);
  const unitOaFileInputRef = useRef(null);
  const hasHydratedOpenModalRef = useRef(false);
  const { session } = usePipeline();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      hasHydratedOpenModalRef.current = false;
      return;
    }
    if (hasHydratedOpenModalRef.current) return;
    hasHydratedOpenModalRef.current = true;

    const initialUnits = Array.isArray(initialData.units) && initialData.units.length
      ? initialData.units.map((unit, index) => makeConsumptionUnit(index + 1, DEFAULT_SLOT_ROWS, unit))
      : [makeConsumptionUnit(1)];

    setNodeName(initialData.name || '');
    setInputMode(initialData.mode || 'hourly_csv');
    setSelectedFile(null);
    setFileName(initialData.fileName || '');
    setSlotCsvFile(null);
    setSlotCsvFileName('');
    setYear(initialData.year || 2025);
    setBankingPolicyName(initialData.bankingPolicyName || 'GH_2025');
    setExistingOaReUnits(initialData.existingOaReUnits ?? 0);
    setExistingBtmUnits(initialData.existingBtmUnits ?? 0);
    setExistingOaType(normalizeExistingOaType(initialData.existingOaType));
    setExistingOaInputMode(normalizeExistingOaInputMode(initialData.existingOaInputMode));
    setExistingOaFile(null);
    setExistingOaFileName(initialData.existingOaFileName || '');
    setExistingOaBankingEnabled(Boolean(initialData.existingOaBankingEnabled));
    setExistingOaBankingPolicy(initialData.existingOaBankingPolicy || '');
    setBalanceMode(normalizeMultiUnitBalanceMode(initialData.balanceMode));
    setGrid(makeEmptyGrid(DEFAULT_SLOT_ROWS, parseStoredSlotMonthly(initialData.slotMonthlyData)));
    setUnits(initialUnits);
    setActiveUnitId(initialUnits[0]?.unit_id);
    setUnitSlotCsvNames({});
    setSubmitError(null);
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getBankingPolicySlots()
      .then((response) => {
        if (cancelled) return;
        const policies = Array.isArray(response?.policies) ? response.policies : [];
        setBankingPolicies(policies);
        if (policies.length && !policies.some((policy) => policy.policy_name === bankingPolicyName)) {
          setBankingPolicyName(policies[0].policy_name);
        }
      })
      .catch((error) => {
        console.error('Error loading banking policy slots:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, bankingPolicyName]);

  const selectedPolicy = useMemo(
    () => bankingPolicies.find((policy) => policy.policy_name === bankingPolicyName),
    [bankingPolicies, bankingPolicyName]
  );
  const slotRows = useMemo(
    () => (selectedPolicy?.slots?.length ? selectedPolicy.slots : DEFAULT_SLOT_ROWS),
    [selectedPolicy]
  );
  const slotDetailsByName = useMemo(() => {
    const details = Array.isArray(selectedPolicy?.slotDetails) ? selectedPolicy.slotDetails : [];
    return details.reduce((acc, detail) => {
      if (detail?.name) acc[detail.name] = detail;
      return acc;
    }, {});
  }, [selectedPolicy]);
  const getSlotDisplayName = (slot) => slotDetailsByName[slot]?.displayName || slot;

  useEffect(() => {
    setGrid((prev) => makeEmptyGrid(slotRows, prev));
    setUnits((prev) => prev.map((unit) => ({
      ...unit,
      slot_monthly_data: makeEmptyGrid(slotRows, unit.slot_monthly_data),
    })));
  }, [slotRows]);

  const activeUnit = useMemo(
    () => units.find((unit) => unit.unit_id === activeUnitId) || units[0],
    [units, activeUnitId]
  );
  const activeGrid = inputMode === 'multi_unit_slot_monthly'
    ? (activeUnit?.slot_monthly_data || makeEmptyGrid(slotRows))
    : grid;

  const monthTotals = useMemo(() => MONTHS.reduce((acc, month) => {
    acc[month] = slotRows.reduce((sum, slot) => {
      const raw = activeGrid[slot]?.[month];
      const value = raw === '' ? 0 : Number(String(raw).replace(/,/g, ''));
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    return acc;
  }, {}), [activeGrid, slotRows]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleExistingOaFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setExistingOaFile(file);
    setExistingOaFileName(file.name);
    event.target.value = '';
  };

  const handleUnitExistingOaFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeUnit) return;
    setUnits((prev) => prev.map((unit) => (
      unit.unit_id === activeUnit.unit_id
        ? {
            ...unit,
            existing_oa_input_mode: 'hourly_csv',
            existing_oa_hourly_filename: file.name,
            existingOaFile: file,
          }
        : unit
    )));
    event.target.value = '';
  };

  const handleSlotFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSubmitError(null);
    try {
      const parsedGrid = await parseCompactSlotCsvFile(file, slotRows, slotDetailsByName);
      setGrid(parsedGrid);
      setSlotCsvFile(null);
      setSlotCsvFileName(file.name);
    } catch (error) {
      console.error('Failed parsing compact slot CSV:', error);
      setSlotCsvFile(null);
      setSlotCsvFileName('');
      setSubmitError({
        title: 'CSV parse failed',
        message: error?.message || 'Could not read the compact slot CSV.',
      });
    } finally {
      event.target.value = '';
    }
  };

  const activeUnitSlotCsvName = activeUnit?.unit_id
    ? (unitSlotCsvNames[activeUnit.unit_id] || '')
    : '';

  const handleMultiUnitSlotFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeUnit) return;
    setIsParsingUnitCsv(true);
    setSubmitError(null);
    try {
      const parsedGrid = await parseCompactSlotCsvFile(file, slotRows, slotDetailsByName);
      setUnits((prev) => prev.map((unit) => (
        unit.unit_id === activeUnit.unit_id
          ? { ...unit, slot_monthly_data: parsedGrid }
          : unit
      )));
      setUnitSlotCsvNames((prev) => ({
        ...prev,
        [activeUnit.unit_id]: file.name,
      }));
    } catch (error) {
      console.error('Failed parsing unit compact slot CSV:', error);
      setSubmitError({
        title: 'CSV parse failed',
        message: error?.message || 'Could not read the compact slot CSV for this unit.',
      });
    } finally {
      setIsParsingUnitCsv(false);
      event.target.value = '';
    }
  };

  const setGridCell = (slot, month, value) => {
    if (inputMode === 'multi_unit_slot_monthly' && activeUnit) {
      setUnits((prev) => prev.map((unit) => (
        unit.unit_id === activeUnit.unit_id
          ? {
              ...unit,
              slot_monthly_data: {
                ...(unit.slot_monthly_data || {}),
                [slot]: {
                  ...((unit.slot_monthly_data || {})[slot] || {}),
                  [month]: value,
                },
              },
            }
          : unit
      )));
      return;
    }
    setGrid((prev) => ({
      ...prev,
      [slot]: {
        ...(prev[slot] || {}),
        [month]: value,
      },
    }));
  };

  const buildGridPayload = (gridToBuild = activeGrid) => slotRows.map((slot) => ({
    Slot: slot,
    ...MONTHS.reduce((acc, month) => {
      acc[month] = gridToBuild[slot]?.[month] ?? '';
      return acc;
    }, {}),
  }));

  const validateGrid = (gridToValidate = activeGrid, label = '') => {
    const providedMonths = [];
    for (const month of MONTHS) {
      const filled = slotRows.map((slot) => String(gridToValidate[slot]?.[month] ?? '').trim() !== '');
      if (filled.some(Boolean) && !filled.every(Boolean)) {
        return { valid: false, message: `${label}${month} must provide values for all policy slots.` };
      }
      if (filled.every(Boolean)) {
        providedMonths.push(month);
      }
    }
    if (!VALID_MONTH_COUNTS.has(providedMonths.length)) {
      return { valid: false, message: `${label}Provide exactly 1, 3, 6, or 12 complete months.` };
    }
    for (const slot of slotRows) {
      for (const month of providedMonths) {
        const value = Number(String(gridToValidate[slot]?.[month]).replace(/,/g, ''));
        if (!Number.isFinite(value) || value < 0) {
          return { valid: false, message: `${label}Enter a non-negative number for ${getSlotDisplayName(slot)}, ${month}.` };
        }
      }
    }
    return { valid: true, message: '' };
  };

  const addUnit = () => {
    const nextUnit = makeConsumptionUnit(units.length + 1, slotRows);
    setUnits((prev) => [
      ...prev.map((unit) => ({ ...unit, allocation_share_override_pct: null })),
      nextUnit,
    ]);
    setActiveUnitId(nextUnit.unit_id);
  };

  const renameActiveUnit = (name) => {
    if (!activeUnit) return;
    setUnits((prev) => prev.map((unit) => (
      unit.unit_id === activeUnit.unit_id ? { ...unit, unit_name: name } : unit
    )));
  };

  const updateActiveUnit = (field, value) => {
    if (!activeUnit) return;
    setUnits((prev) => prev.map((unit) => (
      unit.unit_id === activeUnit.unit_id ? { ...unit, [field]: value } : unit
    )));
  };

  const duplicateActiveUnit = () => {
    if (!activeUnit) return;
    const duplicated = {
      ...activeUnit,
      unit_id: makeUnitId(),
      unit_name: `${activeUnit.unit_name || 'Unit'} copy`,
      allocation_share_override_pct: null,
      slot_monthly_data: makeEmptyGrid(slotRows, activeUnit.slot_monthly_data),
    };
    setUnits((prev) => [
      ...prev.map((unit) => ({ ...unit, allocation_share_override_pct: null })),
      duplicated,
    ]);
    setActiveUnitId(duplicated.unit_id);
  };

  const deleteActiveUnit = () => {
    if (!activeUnit || units.length <= 1) return;
    const confirmed = window.confirm(`Delete ${activeUnit.unit_name || 'this unit'}?`);
    if (!confirmed) return;
    const deletedId = activeUnit.unit_id;
    const filtered = units
      .filter((unit) => unit.unit_id !== deletedId)
      .map((unit) => ({ ...unit, allocation_share_override_pct: null }));
    setUnits(filtered);
    setActiveUnitId(filtered[0]?.unit_id);
    setUnitSlotCsvNames((prev) => {
      const next = { ...prev };
      delete next[deletedId];
      return next;
    });
  };

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      if (!nodeName || nodeName.trim() === '') {
        setSubmitError({ title: 'Missing node name', message: 'Please enter a valid node name.' });
        return;
      }

      const position = initialData.position || { x: 0, y: 0 };
      const yearNumber = Number(year);
      if (!Number.isInteger(yearNumber) || yearNumber < 1900 || yearNumber > 2200) {
        setSubmitError({ title: 'Invalid year', message: 'Please enter a year between 1900 and 2200.' });
        return;
      }
      const oaReNumber = Number(existingOaReUnits || 0);
      const btmNumber = Number(existingBtmUnits || 0);
      if (!Number.isFinite(oaReNumber) || oaReNumber < 0 || !Number.isFinite(btmNumber) || btmNumber < 0) {
        setSubmitError({ title: 'Invalid existing RE', message: 'Existing renewable generation must be non-negative.' });
        return;
      }
      if (inputMode !== 'multi_unit_slot_monthly' && existingOaInputMode === 'hourly_csv' && !existingOaFile && !existingOaFileName) {
        setSubmitError({ title: 'Missing existing OA CSV', message: 'Upload an hourly generation CSV for existing OA, or switch back to an annual total.' });
        return;
      }

      let fileToUpload = selectedFile;
      let slotMonthlyData = null;
      let unitOaFiles = [];
      const gridHasValues = slotRows.some((slot) => MONTHS.some((month) => String(activeGrid[slot]?.[month] ?? '').trim() !== ''));
      if (inputMode === 'hourly_csv') {
        if (!selectedFile && !(initialData.isEditing && fileName)) {
          setSubmitError({ title: 'Missing file', message: 'Please select a CSV file.' });
          return;
        }
      } else if (inputMode === 'slot_monthly') {
        fileToUpload = slotCsvFile;
        const keepStoredCsv = Boolean(initialData.isEditing && fileName && !slotCsvFile && !gridHasValues);
        if (!slotCsvFile && !keepStoredCsv) {
          const validation = validateGrid();
          if (!validation.valid) {
            setSubmitError({ title: 'Check monthly totals', message: validation.message });
            return;
          }
          slotMonthlyData = buildGridPayload();
        }
      } else {
        fileToUpload = null;
        if (!units.length) {
          setSubmitError({ title: 'Missing units', message: 'Add at least one consumption unit.' });
          return;
        }
        const names = new Set();
        const payloadUnits = [];
        for (const [idx, unit] of units.entries()) {
          const unitName = String(unit.unit_name || '').trim();
          if (!unitName) {
            setSubmitError({ title: 'Missing unit name', message: `Enter a name for Unit ${idx + 1}.` });
            return;
          }
          if (names.has(unitName.toLowerCase())) {
            setSubmitError({ title: 'Duplicate unit name', message: `Unit name "${unitName}" is used more than once.` });
            return;
          }
          names.add(unitName.toLowerCase());
          const validation = validateGrid(unit.slot_monthly_data, `${unitName}: `);
          if (!validation.valid) {
            setSubmitError({ title: 'Check unit monthly totals', message: validation.message });
            return;
          }
          const unitOaMode = normalizeExistingOaInputMode(unit.existing_oa_input_mode);
          const unitOaFile = unit.existingOaFile || null;
          if (unitOaMode === 'hourly_csv' && !unitOaFile && !unit.existing_oa_hourly_file) {
            setSubmitError({
              title: 'Missing existing OA CSV',
              message: `Upload an hourly generation CSV for ${unitName}, or switch that unit to an annual total.`,
            });
            return;
          }
          if (unitOaFile) {
            unitOaFiles.push(unitOaFile);
          }
          payloadUnits.push({
            unit_id: unit.unit_id,
            unit_name: unitName,
            location: unit.location || '',
            cd: unit.cd === '' ? 0 : Number(unit.cd || 0),
            ct: unit.ct === '' ? 0 : Number(unit.ct || 0),
            voltage: unit.voltage === '' ? 0 : Number(unit.voltage || 0),
            permissible_limit: unit.permissible_limit,
            existing_oa: unit.existing_oa,
            existing_oa_re_units: Number(unit.existing_oa_re_units || 0),
            existing_btm_units: Number(unit.existing_btm_units || 0),
            existing_oa_type: normalizeExistingOaType(unit.existing_oa_type),
            existing_oa_input_mode: unitOaMode,
            existing_oa_hourly_file: unit.existing_oa_hourly_file || '',
            existing_oa_hourly_filename: unitOaFile?.name || unit.existing_oa_hourly_filename || '',
            existing_oa_banking_enabled: Boolean(unit.existing_oa_banking_enabled),
            existing_oa_banking_policy: unit.existing_oa_banking_policy || '',
            balance: Number(unit.balance || 0),
            balance_mode: balanceMode,
            allocation_share_override_pct: unit.allocation_share_override_pct,
            slot_monthly_data: buildGridPayload(unit.slot_monthly_data),
            banking_policy_name: bankingPolicyName,
          });
        }
        slotMonthlyData = {
          balance_mode: balanceMode,
          units: payloadUnits,
        };
      }

      setIsSubmitting(true);
      let configDataResponse = null;

      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          const response = await appendConsumptionNodeToPipeline(
            session.pipeline_name,
            nodeName,
            fileToUpload,
            position.x,
            position.y,
            {
              mode: inputMode,
              year: yearNumber,
              slotMonthlyData,
              bankingPolicyName,
              replaceExisting: Boolean(initialData.isEditing),
              existingOaReUnits: oaReNumber,
              existingBtmUnits: btmNumber,
              existingOaType,
              existingOaInputMode,
              existingOaFile,
              existingOaFiles: unitOaFiles,
              existingOaBankingEnabled,
              existingOaBankingPolicy: existingOaBankingPolicy || bankingPolicyName,
            }
          );
          configDataResponse = response;
        } catch (apiError) {
          console.error('Error calling appendConsumptionNodeToPipeline:', apiError);
          const info = notifyError(apiError, { operation: OP.node.appendConsumptionInput, showToast: false });
          setSubmitError(info);
          setIsSubmitting(false);
          return;
        }
      }

      onConfigured({
        name: nodeName,
        file: fileToUpload,
        retainedFileName: fileToUpload?.name || fileName,
        slotMonthlyData,
        mode: inputMode,
        year: yearNumber,
        preview: configDataResponse?.consumption_preview || null,
        bankingPolicyName,
        existingOaReUnits: oaReNumber,
        existingBtmUnits: btmNumber,
        existingOaType,
        existingOaInputMode,
        existingOaFileName: existingOaFile?.name || existingOaFileName,
        existingOaBankingEnabled,
        existingOaBankingPolicy: existingOaBankingPolicy || bankingPolicyName,
        balanceMode,
        units: inputMode === 'multi_unit_slot_monthly' ? slotMonthlyData.units : undefined,
      });

      setIsSubmitting(false);
    } catch (error) {
      console.error('Error configuring consumption node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Consumption Configuration"
    >
      <div className="consumption-modal">
        <button onClick={onClose} className="consumption-modal-close">
          <IoCloseSharp size={24} />
        </button>

        <h2 className="consumption-modal-title">
          {initialData.isEditing
            ? (inputMode === 'multi_unit_slot_monthly' ? 'Edit Multi-unit Consumption' : 'Edit Consumption')
            : 'Consumption Configuration'}
        </h2>

        {submitError && (
          <div role="alert" className="consumption-modal-error">
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}

        <div className="consumption-modal-field">
          <label className="consumption-modal-label">Node Name:</label>
          <input
            type="text"
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            className="consumption-modal-input"
            placeholder="Enter node name"
            disabled={initialData.isEditing}
          />
        </div>

        <div className="consumption-modal-field">
          <label className="consumption-modal-label">Input Type:</label>
          <div className="consumption-segmented-control">
            <button
              type="button"
              className={inputMode === 'hourly_csv' ? 'active' : ''}
              onClick={() => setInputMode('hourly_csv')}
              disabled={initialData.isEditing}
            >
              Hourly CSV
            </button>
            <button
              type="button"
              className={inputMode === 'slot_monthly' ? 'active' : ''}
              onClick={() => setInputMode('slot_monthly')}
              disabled={initialData.isEditing}
            >
              Monthly slot totals
            </button>
            <button
              type="button"
              className={inputMode === 'multi_unit_slot_monthly' ? 'active' : ''}
              onClick={() => setInputMode('multi_unit_slot_monthly')}
              disabled={initialData.isEditing}
            >
              Multi-unit monthly
            </button>
          </div>
        </div>

        {inputMode !== 'multi_unit_slot_monthly' && (
          <div className="consumption-modal-field">
            <label className="consumption-modal-label">Existing OA generation:</label>
            <div className="consumption-segmented-control consumption-oa-type-control">
              {EXISTING_OA_TYPES.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={existingOaType === option.id ? 'active' : ''}
                  onClick={() => setExistingOaType(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="consumption-segmented-control" style={{ marginTop: 8 }}>
              <button
                type="button"
                className={existingOaInputMode === 'annual' ? 'active' : ''}
                onClick={() => setExistingOaInputMode('annual')}
              >
                Annual total
              </button>
              <button
                type="button"
                className={existingOaInputMode === 'hourly_csv' ? 'active' : ''}
                onClick={() => setExistingOaInputMode('hourly_csv')}
              >
                Hourly CSV
              </button>
            </div>
            {existingOaInputMode === 'hourly_csv' ? (
              <div className="file-upload-container" style={{ marginTop: 8 }}>
                <input
                  type="file"
                  ref={existingOaFileInputRef}
                  onChange={handleExistingOaFileChange}
                  accept=".csv"
                  style={{ display: 'none' }}
                />
                <div className="file-upload-input-container">
                  <input
                    type="text"
                    value={existingOaFileName}
                    readOnly
                    className="consumption-modal-input file-name-display"
                    placeholder={`Upload hourly ${existingOaType} OA generation CSV`}
                  />
                  <button type="button" onClick={() => existingOaFileInputRef.current.click()} className="browse-button">
                    <FiUpload size={18} />
                    Browse
                  </button>
                </div>
                <div className="file-requirements">{existingOaCsvHint(existingOaType)}</div>
              </div>
            ) : (
              <label className="consumption-existing-re-field">
                Existing OA {existingOaType === 'hybrid' ? 'Solar/Wind' : existingOaType === 'wind' ? 'Wind' : 'Solar'} annual units
                <input
                  type="number"
                  min="0"
                  value={existingOaReUnits}
                  onChange={(e) => setExistingOaReUnits(e.target.value)}
                  className="consumption-modal-input"
                />
              </label>
            )}
            <label className="consumption-existing-re-field">
              Existing Rooftop Solar (BTM)
              <input
                type="number"
                min="0"
                value={existingBtmUnits}
                onChange={(e) => setExistingBtmUnits(e.target.value)}
                className="consumption-modal-input"
              />
            </label>
            <div className="file-requirements">
              {existingOaBankingEnabled
                ? 'Existing OA is matched and banked under the selected policy before proposal settlement; leftover demand after OA banking is what the proposed project matches.'
                : existingOaInputMode === 'hourly_csv'
                  ? 'Hourly existing OA is subtracted timestamp-by-timestamp. BTM annual units are still deducted proportionally.'
                  : 'Annual units are deducted proportionally from consumption before proposal settlement.'}
            </div>
            <label className="consumption-existing-re-field consumption-oa-banking-toggle">
              <input
                type="checkbox"
                checked={existingOaBankingEnabled}
                onChange={(e) => setExistingOaBankingEnabled(e.target.checked)}
              />
              Bank Existing OA (TOD/monthly settlement) before proposal matching
            </label>
            {existingOaBankingEnabled && (
              <div className="consumption-modal-field">
                <label className="consumption-modal-label">Existing OA banking policy:</label>
                <select
                  value={existingOaBankingPolicy || bankingPolicyName}
                  onChange={(e) => setExistingOaBankingPolicy(e.target.value)}
                  className="consumption-modal-select"
                >
                  {bankingPolicies.length ? (
                    bankingPolicies.map((policy) => (
                      <option key={policy.policy_name} value={policy.policy_name}>
                        {policy.label || policy.policy_name}
                      </option>
                    ))
                  ) : (
                    <option value="GH_2025">GH 2025</option>
                  )}
                </select>
                <div className="file-requirements">
                  Defaults to this node's consumption banking policy. Change it only if Existing OA settles under a different policy.
                </div>
              </div>
            )}
          </div>
        )}

        {inputMode === 'hourly_csv' ? (
          <div className="consumption-modal-field">
            <label className="consumption-modal-label">Consumption Data File (CSV):</label>
            <div className="file-upload-container">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv"
                style={{ display: 'none' }}
              />
              <div className="file-upload-input-container">
                <input
                  type="text"
                  value={fileName}
                  readOnly
                  className="consumption-modal-input file-name-display"
                  placeholder="No file selected"
                />
                <button type="button" onClick={() => fileInputRef.current.click()} className="browse-button">
                  <FiUpload size={18} />
                  Browse
                </button>
              </div>
              <div className="file-requirements">File must be CSV format with Date, Time, and Month columns</div>
            </div>
          </div>
        ) : (
          <>
            <div className="consumption-modal-field consumption-year-row">
              <label className="consumption-modal-label">Year:</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="consumption-modal-input"
                min="1900"
                max="2200"
              />
            </div>

            <div className="consumption-modal-field">
              <label className="consumption-modal-label">Banking Policy:</label>
              <select
                value={bankingPolicyName}
                onChange={(e) => setBankingPolicyName(e.target.value)}
                className="consumption-modal-select"
              >
                {bankingPolicies.length ? (
                  bankingPolicies.map((policy) => (
                    <option key={policy.policy_name} value={policy.policy_name}>
                      {policy.label || policy.policy_name}
                    </option>
                  ))
                ) : (
                  <option value="GH_2025">GH 2025</option>
                )}
              </select>
              <div className="file-requirements">Monthly slot rows are derived from this policy's time slot definition.</div>
            </div>

            {inputMode === 'multi_unit_slot_monthly' ? (
              <>
                <div className="consumption-unit-tabs">
                  <div className="consumption-unit-tab-list">
                    {units.map((unit) => (
                      <button
                        key={unit.unit_id}
                        type="button"
                        className={unit.unit_id === activeUnit?.unit_id ? 'active' : ''}
                        onClick={() => setActiveUnitId(unit.unit_id)}
                      >
                        {unit.unit_name || 'Unit'}
                      </button>
                    ))}
                  </div>
                  <div className="consumption-unit-actions">
                    <button type="button" onClick={addUnit}>Add unit</button>
                    <button type="button" onClick={duplicateActiveUnit}>Duplicate</button>
                    <button type="button" onClick={deleteActiveUnit} disabled={units.length <= 1}>Delete</button>
                  </div>
                </div>

                <div className="consumption-modal-field">
                  <label className="consumption-modal-label">Active unit name:</label>
                  <input
                    type="text"
                    value={activeUnit?.unit_name || ''}
                    onChange={(e) => renameActiveUnit(e.target.value)}
                    className="consumption-modal-input"
                  />
                </div>

                <div className="consumption-modal-field">
                  <label className="consumption-modal-label">Existing OA generation for {activeUnit?.unit_name || 'this unit'}:</label>
                  <div className="consumption-segmented-control consumption-oa-type-control">
                    {EXISTING_OA_TYPES.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={normalizeExistingOaType(activeUnit?.existing_oa_type) === option.id ? 'active' : ''}
                        onClick={() => updateActiveUnit('existing_oa_type', option.id)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="consumption-segmented-control" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className={normalizeExistingOaInputMode(activeUnit?.existing_oa_input_mode) === 'annual' ? 'active' : ''}
                      onClick={() => updateActiveUnit('existing_oa_input_mode', 'annual')}
                    >
                      Annual total
                    </button>
                    <button
                      type="button"
                      className={normalizeExistingOaInputMode(activeUnit?.existing_oa_input_mode) === 'hourly_csv' ? 'active' : ''}
                      onClick={() => updateActiveUnit('existing_oa_input_mode', 'hourly_csv')}
                    >
                      Hourly CSV
                    </button>
                  </div>
                  {normalizeExistingOaInputMode(activeUnit?.existing_oa_input_mode) === 'hourly_csv' ? (
                    <div className="file-upload-container" style={{ marginTop: 8 }}>
                      <input
                        key={`${activeUnit?.unit_id}-oa`}
                        type="file"
                        ref={unitOaFileInputRef}
                        onChange={handleUnitExistingOaFileChange}
                        accept=".csv"
                        style={{ display: 'none' }}
                      />
                      <div className="file-upload-input-container">
                        <input
                          type="text"
                          value={activeUnit?.existing_oa_hourly_filename || ''}
                          readOnly
                          className="consumption-modal-input file-name-display"
                          placeholder={`Upload hourly ${normalizeExistingOaType(activeUnit?.existing_oa_type)} OA generation CSV`}
                        />
                        <button type="button" onClick={() => unitOaFileInputRef.current?.click()} className="browse-button">
                          <FiUpload size={18} />
                          Browse
                        </button>
                      </div>
                      <div className="file-requirements">{existingOaCsvHint(normalizeExistingOaType(activeUnit?.existing_oa_type))}</div>
                    </div>
                  ) : (
                    <label className="consumption-existing-re-field">
                      Existing OA annual units
                      <input
                        type="number"
                        min="0"
                        value={activeUnit?.existing_oa_re_units ?? 0}
                        onChange={(e) => updateActiveUnit('existing_oa_re_units', e.target.value)}
                        className="consumption-modal-input"
                      />
                    </label>
                  )}
                  <label className="consumption-existing-re-field">
                    Existing Rooftop Solar (BTM)
                    <input
                      type="number"
                      min="0"
                      value={activeUnit?.existing_btm_units ?? 0}
                      onChange={(e) => updateActiveUnit('existing_btm_units', e.target.value)}
                      className="consumption-modal-input"
                    />
                  </label>
                  <label className="consumption-existing-re-field consumption-oa-banking-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(activeUnit?.existing_oa_banking_enabled)}
                      onChange={(e) => updateActiveUnit('existing_oa_banking_enabled', e.target.checked)}
                    />
                    Bank Existing OA (TOD/monthly settlement) before proposal matching
                  </label>
                  {activeUnit?.existing_oa_banking_enabled && (
                    <div className="consumption-modal-field">
                      <label className="consumption-modal-label">Existing OA banking policy for {activeUnit?.unit_name || 'this unit'}:</label>
                      <select
                        value={activeUnit?.existing_oa_banking_policy || bankingPolicyName}
                        onChange={(e) => updateActiveUnit('existing_oa_banking_policy', e.target.value)}
                        className="consumption-modal-select"
                      >
                        {bankingPolicies.length ? (
                          bankingPolicies.map((policy) => (
                            <option key={policy.policy_name} value={policy.policy_name}>
                              {policy.label || policy.policy_name}
                            </option>
                          ))
                        ) : (
                          <option value="GH_2025">GH 2025</option>
                        )}
                      </select>
                      <div className="file-requirements">
                        Defaults to this unit's consumption banking policy. Change it only if this unit's Existing OA settles under a different policy.
                      </div>
                    </div>
                  )}
                </div>

                <div className="consumption-modal-field">
                  <label className="consumption-modal-label">
                    Compact slot CSV for {activeUnit?.unit_name || 'this unit'} (optional):
                  </label>
                  <div className="file-upload-container">
                    <input
                      key={activeUnit?.unit_id}
                      type="file"
                      ref={multiUnitSlotFileInputRef}
                      onChange={handleMultiUnitSlotFileChange}
                      accept=".csv"
                      style={{ display: 'none' }}
                    />
                    <div className="file-upload-input-container">
                      <input
                        type="text"
                        value={isParsingUnitCsv ? 'Parsing CSV...' : activeUnitSlotCsvName}
                        readOnly
                        className="consumption-modal-input file-name-display"
                        placeholder="Use grid below, or upload Slot, Jan-Dec CSV for this unit"
                      />
                      <button
                        type="button"
                        onClick={() => multiUnitSlotFileInputRef.current?.click()}
                        className="browse-button"
                        disabled={isParsingUnitCsv}
                      >
                        <FiUpload size={18} />
                        Browse
                      </button>
                    </div>
                  </div>
                </div>

                <div className="file-requirements">Open Setup Utility after configuring this node to enter permissible limit and existing OA for each unit.</div>
              </>
            ) : (
              <div className="consumption-modal-field">
                <label className="consumption-modal-label">Compact slot CSV (optional):</label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    ref={slotFileInputRef}
                    onChange={handleSlotFileChange}
                    accept=".csv"
                    style={{ display: 'none' }}
                  />
                  <div className="file-upload-input-container">
                    <input
                      type="text"
                      value={slotCsvFileName}
                      readOnly
                      className="consumption-modal-input file-name-display"
                      placeholder="Use grid below, or upload Slot, Jan-Dec CSV"
                    />
                    <button type="button" onClick={() => slotFileInputRef.current.click()} className="browse-button">
                      <FiUpload size={18} />
                      Browse
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="consumption-grid-wrap">
              <table className="consumption-grid">
                <thead>
                  <tr>
                    <th>Slot</th>
                    {MONTHS.map((month) => <th key={month}>{month}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="consumption-total-row">
                    <td>Total units</td>
                    {MONTHS.map((month) => (
                      <td key={month}>{monthTotals[month] ? Math.round(monthTotals[month] * 100) / 100 : ''}</td>
                    ))}
                  </tr>
                  {slotRows.map((slot) => (
                    <tr key={slot}>
                      <td>
                        <div className="consumption-slot-name">{getSlotDisplayName(slot)}</div>
                        {!!slotDetailsByName[slot]?.timeBlocks?.length && (
                          <div className="consumption-slot-time-blocks">
                            {slotDetailsByName[slot].timeBlocks.join(', ')}
                          </div>
                        )}
                      </td>
                      {MONTHS.map((month) => (
                        <td key={`${slot}-${month}`}>
                          <input
                            type="number"
                            min="0"
                            value={activeGrid[slot]?.[month] ?? ''}
                            onChange={(e) => setGridCell(slot, month, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="file-requirements">Fill exactly 1, 3, 6, or 12 complete months. Blank months are generated from the slot averages.</div>
          </>
        )}

        <div className="consumption-modal-button-container">
          <button
            className="consumption-modal-configure-button"
            onClick={handleConfigure}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (initialData.isEditing ? 'Saving...' : 'Configuring...')
              : (initialData.isEditing ? 'Save changes' : 'Configure')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConsumptionConfigModal;
